import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Stack,
  Paper,
  CircularProgress,
} from '@mui/material';
import dayjs from 'dayjs';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MarketInsightsService } from '../../services/marketInsightsService';
import { MarketSummary, MarketEvent, MARKET_EVENT_TYPE_CONFIG } from '../../types/marketInsights';
import { ToastService } from '../../services/toastService';
import SummaryRichTextEditor from '../../components/RichTextEditor/SummaryRichTextEditor';

// ─────────────────────────────────────────────────────────────────────────────
// Tab Panel
// ─────────────────────────────────────────────────────────────────────────────

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary Dialog
// ─────────────────────────────────────────────────────────────────────────────

interface SummaryDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<MarketSummary>) => void;
  initialData?: MarketSummary | null;
  isLoading: boolean;
}

function SummaryDialog({ open, onClose, onSubmit, initialData, isLoading }: SummaryDialogProps) {
  const currentWeek = getISOWeekNumber(new Date());
  const currentYear = new Date().getFullYear();

  const [title, setTitle] = useState(initialData?.Title ?? '');
  const [content, setContent] = useState(initialData?.Content ?? '');
  const [source, setSource] = useState(initialData?.Source ?? '');
  const [disclaimer, setDisclaimer] = useState(initialData?.Disclaimer ?? '');
  const [weekNumber, setWeekNumber] = useState(initialData?.WeekNumber ?? currentWeek);
  const [year, setYear] = useState(initialData?.Year ?? currentYear);
  const [publishedAt, setPublishedAt] = useState(
    initialData?.PublishedAt
      ? dayjs(initialData.PublishedAt).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD')
  );

  // Reset state when dialog opens with new data
  const handleEnter = useCallback(() => {
    setTitle(initialData?.Title ?? '');
    setContent(initialData?.Content ?? '');
    setSource(initialData?.Source ?? '');
    setDisclaimer(initialData?.Disclaimer ?? '');
    setWeekNumber(initialData?.WeekNumber ?? currentWeek);
    setYear(initialData?.Year ?? currentYear);
    setPublishedAt(
      initialData?.PublishedAt
        ? dayjs(initialData.PublishedAt).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD')
    );
  }, [initialData, currentWeek, currentYear]);

  const handleSubmit = () => {
    if (!title.trim() || !content.trim()) return;
    onSubmit({
      Title: title,
      Content: content,
      Source: source,
      Disclaimer: disclaimer,
      WeekNumber: weekNumber,
      Year: year,
      PublishedAt: dayjs(publishedAt).toISOString(),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}
    >
      <DialogTitle>{initialData ? 'Edit Market Summary' : 'Create Market Summary'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Week Number"
              type="number"
              value={weekNumber}
              onChange={(e) => setWeekNumber(Number(e.target.value))}
              slotProps={{ htmlInput: { min: 1, max: 53 } }}
              sx={{ width: 140 }}
            />
            <TextField
              label="Year"
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ width: 140 }}
            />
            <TextField
              label="Published Date"
              type="date"
              value={publishedAt}
              onChange={(e) => setPublishedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
          </Stack>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
              Content *
            </Typography>
            <SummaryRichTextEditor initialContent={content} onChange={setContent} minHeight={200} />
          </Box>
          <TextField
            label="Source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            fullWidth
            placeholder="e.g. ABN AMRO, BCI Markets"
          />
          <TextField
            label="Disclaimer"
            value={disclaimer}
            onChange={(e) => setDisclaimer(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            placeholder="Optional disclaimer text"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !title.trim() || !content.trim()}
        >
          {isLoading ? <CircularProgress size={20} /> : initialData ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Event Dialog
// ─────────────────────────────────────────────────────────────────────────────

interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Partial<MarketEvent>) => void;
  initialData?: MarketEvent | null;
  isLoading: boolean;
}

function EventDialog({ open, onClose, onSubmit, initialData, isLoading }: EventDialogProps) {
  const [title, setTitle] = useState(initialData?.Title ?? '');
  const [description, setDescription] = useState(initialData?.Description ?? '');
  const [eventDate, setEventDate] = useState(initialData?.EventDate ?? '');
  const [eventType, setEventType] = useState(initialData?.EventType ?? 'auction');
  const [color, setColor] = useState(
    initialData?.Color ?? MARKET_EVENT_TYPE_CONFIG.auction.defaultColor
  );

  const handleEnter = useCallback(() => {
    setTitle(initialData?.Title ?? '');
    setDescription(initialData?.Description ?? '');
    setEventDate(initialData?.EventDate ?? '');
    setEventType(initialData?.EventType ?? 'auction');
    setColor(
      initialData?.Color ??
        MARKET_EVENT_TYPE_CONFIG[initialData?.EventType ?? 'auction']?.defaultColor ??
        MARKET_EVENT_TYPE_CONFIG.custom.defaultColor
    );
  }, [initialData]);

  const handleTypeChange = (type: string) => {
    setEventType(type);
    const config = MARKET_EVENT_TYPE_CONFIG[type];
    if (config) {
      setColor(config.defaultColor);
    }
  };

  const handleSubmit = () => {
    if (!title.trim() || !eventDate) return;
    onSubmit({
      Title: title,
      Description: description,
      EventDate: eventDate,
      EventType: eventType,
      Color: color,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}
    >
      <DialogTitle>{initialData ? 'Edit Market Event' : 'Create Market Event'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Event Date"
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            fullWidth
            required
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Event Type"
            select
            value={eventType}
            onChange={(e) => handleTypeChange(e.target.value)}
            fullWidth
          >
            {Object.entries(MARKET_EVENT_TYPE_CONFIG).map(([key, config]) => (
              <MenuItem key={key} value={key}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: config.defaultColor,
                    }}
                  />
                  <span>{config.label}</span>
                </Stack>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Custom Color"
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            fullWidth
            helperText="Override the default type color"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !title.trim() || !eventDate}
        >
          {isLoading ? <CircularProgress size={20} /> : initialData ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export default function MarketInsightsPage() {
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);

  // Summary dialog state
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [editingSummary, setEditingSummary] = useState<MarketSummary | null>(null);

  // Event dialog state
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<MarketEvent | null>(null);

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: summaries = [], isLoading: summariesLoading } = useQuery({
    queryKey: ['market-insights', 'summaries'],
    queryFn: MarketInsightsService.getSummaries,
  });

  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['market-insights', 'events'],
    queryFn: MarketInsightsService.getEvents,
  });

  // ── Mutations ────────────────────────────────────────────────────────────

  const createSummary = useMutation({
    mutationFn: (data: Partial<MarketSummary>) =>
      MarketInsightsService.createSummary(
        data as Omit<MarketSummary, 'ID' | 'CreatedAt' | 'UpdatedAt' | 'CreatedBy'>
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-insights', 'summaries'] });
      ToastService.success('Market summary created successfully');
      setSummaryDialogOpen(false);
    },
    onError: (error) => ToastService.handleApiError(error, 'creating market summary'),
  });

  const updateSummary = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MarketSummary> }) =>
      MarketInsightsService.updateSummary(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-insights', 'summaries'] });
      ToastService.success('Market summary updated successfully');
      setSummaryDialogOpen(false);
      setEditingSummary(null);
    },
    onError: (error) => ToastService.handleApiError(error, 'updating market summary'),
  });

  const deleteSummary = useMutation({
    mutationFn: MarketInsightsService.deleteSummary,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-insights', 'summaries'] });
      ToastService.success('Market summary deleted');
    },
    onError: (error) => ToastService.handleApiError(error, 'deleting market summary'),
  });

  const createEvent = useMutation({
    mutationFn: (data: Partial<MarketEvent>) =>
      MarketInsightsService.createEvent(
        data as Omit<MarketEvent, 'ID' | 'CreatedAt' | 'UpdatedAt' | 'CreatedBy'>
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-insights', 'events'] });
      ToastService.success('Market event created successfully');
      setEventDialogOpen(false);
    },
    onError: (error) => ToastService.handleApiError(error, 'creating market event'),
  });

  const updateEvent = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MarketEvent> }) =>
      MarketInsightsService.updateEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-insights', 'events'] });
      ToastService.success('Market event updated successfully');
      setEventDialogOpen(false);
      setEditingEvent(null);
    },
    onError: (error) => ToastService.handleApiError(error, 'updating market event'),
  });

  const deleteEvent = useMutation({
    mutationFn: MarketInsightsService.deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['market-insights', 'events'] });
      ToastService.success('Market event deleted');
    },
    onError: (error) => ToastService.handleApiError(error, 'deleting market event'),
  });

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleCreateSummary = () => {
    setEditingSummary(null);
    setSummaryDialogOpen(true);
  };

  const handleEditSummary = (summary: MarketSummary) => {
    setEditingSummary(summary);
    setSummaryDialogOpen(true);
  };

  const handleSubmitSummary = (data: Partial<MarketSummary>) => {
    if (editingSummary) {
      updateSummary.mutate({ id: editingSummary.ID, data });
    } else {
      createSummary.mutate(data);
    }
  };

  const handleDeleteSummary = (id: string) => {
    if (window.confirm('Are you sure you want to delete this market summary?')) {
      deleteSummary.mutate(id);
    }
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setEventDialogOpen(true);
  };

  const handleEditEvent = (event: MarketEvent) => {
    setEditingEvent(event);
    setEventDialogOpen(true);
  };

  const handleSubmitEvent = (data: Partial<MarketEvent>) => {
    if (editingEvent) {
      updateEvent.mutate({ id: editingEvent.ID, data });
    } else {
      createEvent.mutate(data);
    }
  };

  const handleDeleteEvent = (id: string) => {
    if (window.confirm('Are you sure you want to delete this market event?')) {
      deleteEvent.mutate(id);
    }
  };

  // ── Column Definitions ───────────────────────────────────────────────────

  const summaryColumns: GridColDef[] = [
    {
      field: 'WeekYear',
      headerName: 'Week / Year',
      width: 120,
      valueGetter: (_value, row) => `W${row.WeekNumber} ${row.Year}`,
    },
    { field: 'Title', headerName: 'Title', flex: 1, minWidth: 200 },
    { field: 'Source', headerName: 'Source', width: 150 },
    {
      field: 'PublishedAt',
      headerName: 'Published',
      width: 160,
      valueFormatter: (value: string) => (value ? new Date(value).toLocaleDateString() : '—'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={() => handleEditSummary(params.row as MarketSummary)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteSummary(params.row.ID)}>
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const eventColumns: GridColDef[] = [
    {
      field: 'EventDate',
      headerName: 'Date',
      width: 120,
      valueFormatter: (value: string) =>
        value ? new Date(value + 'T00:00:00').toLocaleDateString() : '—',
    },
    { field: 'Title', headerName: 'Title', flex: 1, minWidth: 200 },
    {
      field: 'EventType',
      headerName: 'Type',
      width: 160,
      renderCell: (params) => {
        const config = MARKET_EVENT_TYPE_CONFIG[params.value as string];
        const label = config?.label ?? params.value;
        const chipColor = (params.row as MarketEvent).Color || config?.defaultColor || '#757575';
        return (
          <Chip
            label={label}
            size="small"
            sx={{ bgcolor: chipColor, color: '#fff', fontWeight: 500 }}
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={() => handleEditEvent(params.row as MarketEvent)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDeleteEvent(params.row.ID)}>
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Market Insights
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage weekly market summaries and event calendar entries displayed to users.
      </Typography>

      <Paper sx={{ p: 0 }}>
        <Tabs
          value={tabValue}
          onChange={(_e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Weekly Summaries" />
          <Tab label="Event Calendar" />
        </Tabs>

        {/* Summaries Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button variant="contained" startIcon={<Add />} onClick={handleCreateSummary}>
                New Summary
              </Button>
            </Box>
            <DataGrid
              rows={summaries}
              columns={summaryColumns}
              getRowId={(row) => row.ID}
              loading={summariesLoading}
              autoHeight
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              disableRowSelectionOnClick
              sx={{ border: 'none' }}
            />
          </Box>
        </TabPanel>

        {/* Events Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button variant="contained" startIcon={<Add />} onClick={handleCreateEvent}>
                New Event
              </Button>
            </Box>
            <DataGrid
              rows={events}
              columns={eventColumns}
              getRowId={(row) => row.ID}
              loading={eventsLoading}
              autoHeight
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              disableRowSelectionOnClick
              sx={{ border: 'none' }}
            />
          </Box>
        </TabPanel>
      </Paper>

      {/* Dialogs */}
      <SummaryDialog
        open={summaryDialogOpen}
        onClose={() => {
          setSummaryDialogOpen(false);
          setEditingSummary(null);
        }}
        onSubmit={handleSubmitSummary}
        initialData={editingSummary}
        isLoading={createSummary.isPending || updateSummary.isPending}
      />
      <EventDialog
        open={eventDialogOpen}
        onClose={() => {
          setEventDialogOpen(false);
          setEditingEvent(null);
        }}
        onSubmit={handleSubmitEvent}
        initialData={editingEvent}
        isLoading={createEvent.isPending || updateEvent.isPending}
      />
    </Box>
  );
}
